/**
 * Task 37 — the rules the ConnectWise connection form enforces before anything is sent.
 *
 * KEEP THIS MODULE FREE OF IMPORTS. ClientApp/node_modules is not checked in and NavBar.js cannot be
 * loaded outside a browser, so logic placed in a component is unexecutable in a test. Out here, with
 * pure functions taking their inputs explicitly, it runs under bare node — the same reasoning as
 * Helpers/SuppressionDuration.js and Helpers/NavGeneralMenu.js.
 *
 * These checks are a convenience, not the authority. The server validates the same things again,
 * because a browser is not something to trust about whether a credential is well formed.
 */

/** Every validator returns this shape, with `message` ready to show as-is. */
function ok() {
    return { valid: true, message: null };
}

function bad(message) {
    return { valid: false, message };
}

/**
 * The address of the reseller's ConnectWise API.
 *
 * A missing trailing slash is NOT an error. The engine appends one, because a base address without it
 * silently loses its last path segment when a relative path is resolved against it — so every call
 * would return not-found. An address that looks right to a person should not be refused over a
 * character the software can add itself.
 */
export function validateSiteUrl(siteUrl) {
    const value = (siteUrl || "").trim();
    if (value.length === 0) return bad("Enter the address of your ConnectWise API.");

    let parsed;
    try {
        parsed = new URL(value);
    } catch (e) {
        return bad("That does not look like a web address. It should start with https://");
    }

    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
        return bad("The address should start with https://");
    }

    return ok();
}

export function validateCompanyId(companyId) {
    return (companyId || "").trim().length === 0
        ? bad("Enter your ConnectWise company ID.")
        : ok();
}

export function validatePublicKey(publicKey) {
    return (publicKey || "").trim().length === 0
        ? bad("Enter the public key.")
        : ok();
}

/**
 * Task 37 N6 — the client identifier ConnectWise issues to an integration.
 *
 * Required on every save and test, as the client asked. It used to be optional, falling back to a
 * shared value in configuration that was never filled in, so leaving it blank passed here and failed
 * later at sync time with an error that did not name it.
 */
export function validateClientId(clientId) {
    return (clientId || "").trim().length === 0
        ? bad("Enter your ConnectWise Client ID.")
        : ok();
}

/**
 * The private key.
 *
 * `hasStoredKey` is what makes a blank field acceptable: once a key is saved, leaving this empty keeps
 * it, so somebody correcting a company id does not have to go and find the key again. With nothing
 * stored there is nothing to keep, so a value is required.
 */
export function validatePrivateKey(privateKey, hasStoredKey) {
    if ((privateKey || "").trim().length > 0) return ok();

    return hasStoredKey
        ? ok()
        : bad("Enter the private key.");
}

/**
 * Whether the form may be submitted at all.
 *
 * Deliberately separate from whether SAVE is allowed: saving additionally requires a passed connection
 * test, which only the screen knows about. This answers the narrower question of whether the fields
 * are filled in well enough to be worth sending.
 */
export function validateConnection(form, hasStoredKey) {
    const checks = [
        validateSiteUrl(form && form.siteUrl),
        validateCompanyId(form && form.companyId),
        validatePublicKey(form && form.publicKey),
        validatePrivateKey(form && form.privateKey, hasStoredKey),
        validateClientId(form && form.clientId),
    ];

    const failed = checks.find((c) => !c.valid);
    return failed || ok();
}

/**
 * Whether a connection test can be run.
 *
 * Blank means "use the stored key", the same as it does on save, which is why this takes
 * `hasStoredKey` rather than demanding a key outright. ConnectWise shows a private key once; refusing
 * to test without it would leave a reseller unable to correct their site address without regenerating
 * their key pair — and the server tests the resolved credential either way, so nothing is proven
 * about a value nobody is going to store.
 */
export function canTest(form, hasStoredKey) {
    return validateConnection(form, hasStoredKey);
}
